<template>
  <div
    class="relative h-[50px] bg-[#d4d8e1]/25 overflow-visible tablet-h:rounded-md"
  >
    <div
      class="absolute left-[10px] top-0 bottom-0 flex items-center text-[14px] leading-[16px] text-gray-500 pointer-events-none z-10"
    >
      Mapping
    </div>

    <!-- The viewBox tracks the measured box, so user units are already CSS px
         and stroke-width needs no correction. Do not add non-scaling-stroke:
         Chromium resolves it against device pixels (half width on hidpi). -->
    <svg
      ref="svgRef"
      class="absolute inset-0 w-full h-full overflow-visible"
      preserveAspectRatio="none"
      :viewBox="`0 0 ${width || 1} 50`"
    >
      <g v-for="curve in curves" :key="curve.id">
        <path
          :d="curve.d"
          :stroke="curve.color"
          stroke-width="1.5"
          fill="none"
          pointer-events="none"
          class="transition-[opacity,filter] duration-150"
          :style="styleFor(curve.id)"
        />
      </g>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { TimelineHoverKey, type TimelineMatch } from './timeline-types'

const props = defineProps<{
  matches: readonly TimelineMatch[]
  infringingDuration: number
  registeredDuration: number
  infringingInsetLeft?: number
  infringingInsetRight?: number
  registeredInsetLeft?: number
  registeredInsetRight?: number
}>()

const svgRef = ref<SVGSVGElement>()
const width = ref(0)
let ro: ResizeObserver | null = null

const hover = inject(TimelineHoverKey, null)

const styleFor = (id: string) => {
  const h = hover?.hoveredId.value
  const dimmed = h && h !== id
  return {
    opacity: dimmed ? 0.25 : 1,
    filter: dimmed ? 'grayscale(1)' : 'none',
  }
}

onMounted(() => {
  if (!svgRef.value) return
  width.value = svgRef.value.getBoundingClientRect().width
  ro = new ResizeObserver(([entry]) => {
    width.value = entry.contentRect.width
  })
  ro.observe(svgRef.value)
})

onUnmounted(() => {
  ro?.disconnect()
})

const CURVE_COLOR: Record<ScoreLevel, string> = {
  [ScoreLevel.High]: '#ee888a',
  [ScoreLevel.Medium]: '#f5b958',
  [ScoreLevel.Low]: '#34b1c7',
}

const xFor = (
  time: number,
  duration: number,
  left: number,
  right: number
): number => {
  const w = width.value
  if (!w) return 0
  const usable = w - left - right
  const pct = duration > 0 ? time / duration : 0
  return left + pct * usable
}

const curves = computed(() => {
  const list: Array<{
    id: string
    d: string
    color: string
    match: TimelineMatch
  }> = []
  if (!width.value) return list

  // Timeline track: 60px icon gutter + 1px separator, 6px right gutter
  const infL = props.infringingInsetLeft ?? 61
  const infR = props.infringingInsetRight ?? 6
  const regL = props.registeredInsetLeft ?? 61
  const regR = props.registeredInsetRight ?? 6

  for (const m of props.matches) {
    const infMid = (m.infringing.start + m.infringing.end) / 2
    const regMid = (m.registered.start + m.registered.end) / 2
    const x0 = xFor(infMid, props.infringingDuration, infL, infR)
    const x1 = xFor(regMid, props.registeredDuration, regL, regR)

    const y0 = 0
    const y1 = 50
    const c1x = x0
    const c1y = 25
    const c2x = x1
    const c2y = 25

    const d = `M ${x0.toFixed(2)} ${y0} C ${c1x.toFixed(2)} ${c1y} ${c2x.toFixed(
      2
    )} ${c2y} ${x1.toFixed(2)} ${y1}`

    list.push({
      id: m.id,
      d,
      color: CURVE_COLOR[m.level] ?? CURVE_COLOR[ScoreLevel.High],
      match: m,
    })
  }

  return list
})
</script>
